import api from '@/services/api'
import { getTotalLiquidoFaturas } from '@/services/fatura'
import type { Fatura } from '@/types/fatura'
import { formatDate, formatDatetime, formatMoney } from '@/utils'
import { copyTextToClipboard } from '@/utils'
import { faCopy, faDownload, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { toast } from 'react-toastify'
import AsaasBadge from './AsaasBadge'
import FaturaValorResumo from './FaturaValorResumo'

type TipoPagamentoAberto = 'boleto' | 'pix'

interface DetalhesPagamentoAbertoProps {
    tipo: TipoPagamentoAberto
    faturas: Fatura[]
    onRefresh?: () => Promise<void>
}

export default function DetalhesPagamentoAberto({ tipo, faturas, onRefresh }: DetalhesPagamentoAbertoProps) {
    const [loadingCancelamento, setLoadingCancelamento] = useState(false)

    if (faturas.length === 0) return null

    const faturaPrincipal = faturas[0]
    const boleto = faturaPrincipal.boleto
    const pix = faturaPrincipal.pix
    const valorLiquido = getTotalLiquidoFaturas(faturas)
    const vencimento = tipo === 'boleto'
        ? boleto!.Vencimento
        : pix!.Vencimento
    const linhaDigitavel = boleto?.LinhaDigitavel ?? ''
    const emv = pix?.Emv ?? ''

    const handleCopy = async (value: string, successMessage: string) => {
        const copied = await copyTextToClipboard(value)
        if (copied) {
            toast.success(successMessage)
            return
        }

        toast.error('Não foi possível copiar o conteúdo.')
    }

    const handleCancelarBoleto = async () => {
        const cdBoleto = boleto?.CdBoleto
        if (!cdBoleto) return
        if (!window.confirm('Deseja realmente cancelar o boleto?')) return

        setLoadingCancelamento(true)
        try {
            const res = await api.delete(`/clientes/boletos/${cdBoleto}`)
            if (res.data.situacao === 'Cancelado') {
                if (onRefresh) await onRefresh()
                return
            }

            if (res.data.errors) {
                toast.error(
                    res.data.errors
                        .map((error: any) => error.fields.map((fieldError: any) => fieldError.message).join('\n'))
                        .join('\n')
                )
                return
            }

            toast.error('Ocorreu um erro inesperado.')
        } catch (error: any) {
            const message = error?.response?.data?.errors
                ? error.response.data.errors
                    .map((err: any) => err.fields.map((fieldError: any) => fieldError.message).join('\n'))
                    .join('\n')
                : error?.response?.data?.message

            toast.error(message || 'Ocorreu um erro inesperado.')
        } finally {
            setLoadingCancelamento(false)
        }
    }

    return (
        <div className="pagamento-panel">
            <div className="pagamento-panel-header">
                <h4 className="pagamento-panel-title">
                    Detalhes do {tipo === 'boleto' ? 'boleto' : 'PIX'} em aberto
                </h4>
            </div>

            <FaturaValorResumo faturas={faturas} variant="panel" className="pagamento-panel-valores" />

            <div className="pagamento-form-stack">
                <div className="pagamento-deposito-grid">
                    <div className="pagamento-deposito-field">
                        <label className="form-label">Vencimento</label>
                        <input
                            type="text"
                            className="form-control deposito-field-input"
                            disabled
                            readOnly
                            value={vencimento ? (tipo === 'boleto' ? formatDate(vencimento) : formatDatetime(vencimento)) : '—'}
                        />
                    </div>

                    <div className="pagamento-deposito-field">
                        <label className="form-label">Valor</label>
                        <input
                            type="text"
                            className="form-control deposito-field-input"
                            disabled
                            readOnly
                            value={formatMoney(valorLiquido)}
                        />
                    </div>

                    {tipo === 'boleto' ? (
                        <>
                            <div className="pagamento-deposito-field">
                                <label className="form-label">Linha digitável</label>
                                <div className="copy-field-inline">
                                    <input
                                        type="text"
                                        className="form-control deposito-field-input"
                                        disabled
                                        readOnly
                                        value={linhaDigitavel || '—'}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary copy-field-btn copy-field-btn-inline"
                                        onClick={() => handleCopy(linhaDigitavel, 'Linha digitável copiada com sucesso!')}
                                        disabled={!linhaDigitavel}
                                    >
                                        <FontAwesomeIcon icon={faCopy} className="mr-2" /> Copiar linha digitável
                                    </button>
                                </div>
                            </div>

                            <div className="pagamento-actions" style={{ gap: 8, flexWrap: 'wrap' }}>
                                {boleto?.Link && (
                                    <a
                                        className="btn btn-primary"
                                        href={boleto.Link}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <FontAwesomeIcon icon={faDownload} className="mr-2" /> Baixar boleto
                                    </a>
                                )}
                                {boleto?.CdBoleto && (
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleCancelarBoleto}
                                        disabled={loadingCancelamento}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="mr-2" /> Cancelar boleto
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="pagamento-deposito-field">
                                <label className="form-label">Copia e cola</label>
                                <div className="copy-field-inline">
                                    <input
                                        type="text"
                                        className="form-control deposito-field-input"
                                        disabled
                                        readOnly
                                        value={emv || '—'}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary copy-field-btn copy-field-btn-inline"
                                        onClick={() => handleCopy(emv, 'Código Pix copiado com sucesso!')}
                                        disabled={!emv}
                                    >
                                        <FontAwesomeIcon icon={faCopy} className="mr-2" /> Copiar código Pix
                                    </button>
                                </div>
                            </div>

                            <p className="pagamento-deposito-note">
                                O QR Code e o código Pix podem ser utilizados enquanto este pagamento permanecer em aberto.
                            </p>
                        </>
                    )}
                </div>
            </div>

            <AsaasBadge />
        </div>
    )
}
